import { IconAlertCircle, IconClock } from '@developerz/ui';
import { A } from '@solidjs/router';
import { type JSX, Match, Show, Switch } from 'solid-js';
import { t } from '../i18n';
import { orgHref } from '../lib/org';
import { type PlanStartOutcome, planRefusalKey } from '../lib/plan-start';
import { Button } from './ui/Button';

// What `POST /v1/plans` actually did. Presenter — three readings, and the
// SUCCESS is the one that needs the care.
//
// DISPATCHED IS NOT RUNNING, so this says "dispatched" and wears the waiting
// icon rather than a tick. The 202 answers a task id; a box mints the run id at
// claim time and the plan itself only exists once the scout emits
// `plan_authored`. Every link here therefore points at the QUEUE BOARD, the one
// surface that can show a scout task before either of those has happened —
// pointing at `/fleet/runs/:run_id` would need a run id that does not exist yet,
// and pointing back at this index would show a plan nobody has authored.
//
// A REFUSAL SPEAKS ON TWO CHANNELS, the `<NewTaskRefused/>` shape: the framing
// line is chosen by status (`planRefusalKey`) and the API's own `detail` prints
// under it — which for the two refusals that matter (a repo this account cannot
// plan on, a fleet with no box able to hold a live session) is the port's shared
// sentence, the same words the `plan_start` MCP verb answers with.

export type StartPlanResultProps = {
  readonly outcome: PlanStartOutcome;
  /** Back to an empty form, same modal. */
  readonly onAgain: () => void;
  readonly onClose: () => void;
};

export function StartPlanResult(props: StartPlanResultProps): JSX.Element {
  // why the cast: `kind === K` narrows at runtime, but TS cannot carry that
  // narrowing through a generic type parameter (the `<NewTaskResult/>` idiom).
  const of = <K extends PlanStartOutcome['kind']>(
    kind: K,
  ): Extract<PlanStartOutcome, { kind: K }> | null =>
    props.outcome.kind === kind ? (props.outcome as Extract<PlanStartOutcome, { kind: K }>) : null;

  return (
    <>
      <Switch>
        <Match when={of('started')}>
          {(o) => (
            <div class="alert alert--icon" role="alert">
              <IconClock />
              <p class="alert__title">{t('fleet.plans.start.result_started')}</p>
              <p class="alert__desc">
                {t('fleet.plans.start.result_started_desc', { task: o().task_id })}
              </p>
              <A class="alert__action" href={orgHref('/fleet')}>
                {t('fleet.plans.start.result_open_queue')}
              </A>
            </div>
          )}
        </Match>

        {/* A 2xx that named no task: it may have dispatched, so neither
            "started" nor "try again" is honest — the board is where the
            answer lives. */}
        <Match when={of('unclear')}>
          <div class="alert alert--icon" role="alert">
            <IconAlertCircle />
            <p class="alert__title">{t('fleet.plans.start.result_unclear')}</p>
            <p class="alert__desc">{t('fleet.plans.start.result_unclear_desc')}</p>
            <A class="alert__action" href={orgHref('/fleet')}>
              {t('fleet.plans.start.result_open_queue')}
            </A>
          </div>
        </Match>

        <Match when={of('refused')}>
          {(o) => (
            <div class="alert alert--icon alert--destructive" role="alert">
              <IconAlertCircle />
              <p class="alert__title">{t(planRefusalKey(o().status))}</p>
              <Show when={o().detail}>{(detail) => <p class="alert__desc">{detail()}</p>}</Show>
            </div>
          )}
        </Match>
      </Switch>

      <div class="dialog__footer">
        <Button variant="outline" onClick={props.onAgain}>
          {t('fleet.plans.start.another')}
        </Button>
        <Button onClick={props.onClose}>{t('fleet.plans.start.close')}</Button>
      </div>
    </>
  );
}
